import {useState, useCallback} from "react";
import {
	GridFilterModel,
	GridSortModel,
	GridInitialState,
	GridFilterItem
} from "@mui/x-data-grid-pro";
import {useListQuizzes, type QuizListRow, DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE} from "@/app2/shared/data";
import type {QuizzesControllerListParams} from "@/app2/shared/data/gen";

const PUBLISHED_AT_OPS_WITH_VALUE = new Set<string>([
	"is",
	"not",
	"after",
	"onOrAfter",
	"before",
	"onOrBefore"
]);

function toPublishedAtValueString(value: unknown): string | undefined {
	if (value == null || value === "") {
		return undefined;
	}
	if (value instanceof Date) {
		if (Number.isNaN(value.getTime())) {
			return undefined;
		}
		const y = value.getFullYear();
		const m = String(value.getMonth() + 1).padStart(2, "0");
		const d = String(value.getDate()).padStart(2, "0");
		return `${y}-${m}-${d}`;
	}
	const s = String(value).trim();
	const match = /^(\d{4}-\d{2}-\d{2})/.exec(s);
	return match ? match[1] : undefined;
}

function applyPublishedAtFilter(
	updated: QuizzesControllerListParams,
	publishedItem: GridFilterItem | undefined
) {
	const op = publishedItem?.operatorValue;
	if (!op) {
		delete updated.publishedAtOperator;
		delete updated.publishedAtValue;
		return;
	}
	if (op === "isEmpty" || op === "isNotEmpty") {
		updated.publishedAtOperator = op as QuizzesControllerListParams["publishedAtOperator"];
		delete updated.publishedAtValue;
		return;
	}
	if (!PUBLISHED_AT_OPS_WITH_VALUE.has(op)) {
		delete updated.publishedAtOperator;
		delete updated.publishedAtValue;
		return;
	}
	const dateOnly = toPublishedAtValueString(publishedItem?.value);
	if (!dateOnly) {
		delete updated.publishedAtOperator;
		delete updated.publishedAtValue;
		return;
	}
	updated.publishedAtOperator = op as QuizzesControllerListParams["publishedAtOperator"];
	updated.publishedAtValue = dateOnly;
}

export const useQuizzesListGrid = () => {
	const [filterModel, setFilterModel] = useState<QuizzesControllerListParams>({
		page: 1,
		pageSize: DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
		orderBy: "updatedAt",
		orderDirection: "desc",
		extendedAccess: false
	});

	const {data, isLoading, refetch} = useListQuizzes(filterModel);

	const rows: QuizListRow[] = (data?.data ?? []) as unknown as QuizListRow[];
	const rowCount = data?.totalCount ?? 0;

	const onPageChange = useCallback((page: number) => {
		setFilterModel((prev) => ({
			...prev,
			page: page + 1
		}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setFilterModel((prev) => ({
			...prev,
			pageSize,
			page: 1
		}));
	}, []);

	const onFilterChange = useCallback((newFilterModel: GridFilterModel) => {
		setFilterModel((prev) => {
			const updated: QuizzesControllerListParams = {
				...prev,
				page: 1
			};

			const quickFilter = newFilterModel.quickFilterValues?.[0];
			if (quickFilter !== undefined && quickFilter !== null && quickFilter !== "") {
				updated.searchTerm = String(quickFilter).trim();
			} else {
				const titleItem = newFilterModel.items?.find(
					(item: { columnField?: string }) => item.columnField === "title"
				);
				if (
					titleItem &&
					titleItem.value !== undefined &&
					titleItem.value !== null &&
					String(titleItem.value).trim() !== ""
				) {
					updated.searchTerm = String(titleItem.value).trim();
				} else {
					delete updated.searchTerm;
				}
			}

			const publishedItem = newFilterModel.items?.find(
				(item: GridFilterItem) => item.columnField === "publishedAt"
			);
			applyPublishedAtFilter(updated, publishedItem);

			return updated;
		});
	}, []);

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		setFilterModel((prev) => ({
			...prev,
			orderBy:
				sortModel.length > 0
					? (sortModel[0].field as "title" | "updatedAt")
					: "updatedAt",
			orderDirection:
				sortModel.length > 0
					? (sortModel[0].sort as "asc" | "desc")
					: "desc"
		}));
	}, []);

	const defaultInitialState: GridInitialState = {
		pagination: {
			pageSize: filterModel.pageSize ?? DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
			page: (filterModel.page ?? 1) - 1
		},
		sorting: {
			sortModel: [{field: "updatedAt", sort: "desc"}]
		}
	};

	return {
		rows,
		rowCount,
		isLoading,
		refetch,
		page: (filterModel.page ?? 1) - 1,
		pageSize: filterModel.pageSize ?? DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		defaultInitialState
	};
};
